import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Copy, Check, Loader2, Search, CheckCircle2 } from 'lucide-react';
import { dnsPruefung } from '../api';

/**
 * Was der Kunde in seinem DNS eintragen muss — zum Kopieren.
 *
 * Vorher stand hier nur, DASS jemand einen A-Record setzen muss. Wer das liest,
 * weiss danach, dass etwas zu tun ist, und muss sich Name und Adresse aus zwei
 * anderen Feldern zusammensuchen und abtippen. Eine IP abzutippen ist genau die
 * Sorte Arbeit, bei der ein Zahlendreher entsteht — und der faellt dann nicht
 * als Tippfehler auf, sondern als „die Seite geht nicht".
 *
 * ══ WARUM AUCH AAAA ════════════════════════════════════════════════════════
 *
 * Weil Vergessen hier teuer ist: hat der Wirt eine IPv6-Adresse und fehlt der
 * AAAA-Record, erreichen Besucher mit IPv6 die Seite nicht — und das sind
 * ausgerechnet die, die es zu Hause am ehesten haben. Der Fehler sieht dann
 * aus wie „bei mir geht es doch".
 */

function Kopierbar({ wert, beschriftung }: { wert: string; beschriftung?: string }) {
  const [kopiert, setKopiert] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(wert);
          setKopiert(true);
          setTimeout(() => setKopiert(false), 1500);
        } catch {
          // Ohne Zwischenablage (unsicherer Kontext, verweigerte Erlaubnis)
          // bleibt der Wert sichtbar und markierbar. Eine Fehlermeldung waere
          // hier lauter als das Problem.
        }
      }}
      title="Kopieren"
      className="group inline-flex max-w-full items-center gap-1.5 rounded border border-transparent px-1 py-0.5 text-left text-xs hover:border-slate-300 hover:bg-white"
    >
      {beschriftung ? (
        <span className="font-medium underline decoration-dotted">{beschriftung}</span>
      ) : (
        <code className="truncate font-mono">{wert}</code>
      )}
      {kopiert ? (
        <Check size={11} className="shrink-0 text-emerald-600" />
      ) : (
        <Copy size={11} className="shrink-0 text-slate-400 group-hover:text-slate-600" />
      )}
    </button>
  );
}

export interface DnsAnleitungProps {
  /** Der vollständige Name, der zeigen soll — z. B. `shop.kunde.de` oder `kunde.de`. */
  hostname: string | null;
  /**
   * Die Zone selbst — `kunde.de`.
   *
   * Nur daran ist zu erkennen, ob `hostname` die Domain SELBST ist (Apex) oder
   * eine Subdomain darunter. Ohne das müsste man raten, und „zwei Punkte drin,
   * also Subdomain" ist bei `kunde.co.uk` schon falsch.
   */
  domain?: string | null;
  ipv4: string | null;
  ipv6?: string | null;
  /** Name des Wirts, nur zur Einordnung. */
  serverName?: string | null;
  /** Weitere Namen, die der PC für diese Instanz kennt — für die www-Frage. */
  weitereNamen?: string[];
}

export default function DnsAnleitung({
  hostname,
  domain,
  ipv4,
  ipv6,
  serverName,
  weitereNamen = [],
}: DnsAnleitungProps) {
  // Ohne Namen oder Adresse gibt es nichts Konkretes zu sagen — dann bleibt es
  // beim Hinweis, statt eine halbe Anleitung mit Lücken zu zeigen.
  const vollstaendig = Boolean(hostname && ipv4);

  // ══ APEX ODER SUBDOMAIN ══════════════════════════════════════════════════
  //
  // Bei der Domain selbst heisst der Eintrag in fast jeder DNS-Oberflaeche `@`
  // oder bleibt leer — den vollen Namen dort einzutragen ergibt
  // `kunde.de.kunde.de`. Das passiert oft genug, dass es hier stehen sollte.
  const istApex = Boolean(hostname && domain && hostname === domain);
  /** Was in die Spalte „Name" gehoert. */
  const recordName = istApex ? '@' : hostname && domain && hostname.endsWith('.' + domain)
    ? hostname.slice(0, -(domain.length + 1))
    : hostname ?? '';

  /** Kennt der PC schon eine www-Adresse fuer diese Instanz? */
  const wwwName = domain ? `www.${domain}` : null;
  const wwwEingerichtet = Boolean(wwwName && weitereNamen.includes(wwwName));

  const pruefen = useMutation({
    mutationFn: () => dnsPruefung.pruefen(hostname!, ipv4, ipv6 ?? null),
  });

  /** Alles auf einmal, zum Weiterschicken an den Kunden. */
  const spalte = (w: string) => w.padEnd(22);
  const alles = [
    `DNS-Eintraege fuer ${hostname}`,
    '',
    `Typ    ${spalte('Name')}Wert`,
    `A      ${spalte(recordName)}${ipv4}`,
    ...(ipv6 ? [`AAAA   ${spalte(recordName)}${ipv6}`] : []),
    ...(wwwEingerichtet ? [`A      ${spalte('www')}${ipv4}`] : []),
    ...(wwwEingerichtet && ipv6 ? [`AAAA   ${spalte('www')}${ipv6}`] : []),
    '',
    ...(istApex
      ? [
          'Der Name "@" steht fuer die Domain selbst — in vielen Oberflaechen',
          'bleibt das Namensfeld dafuer einfach leer. Ein CNAME ist dort nicht',
          'zulaessig, nur A und AAAA.',
          '',
        ]
      : []),
    'TTL: Standard genuegt. Sobald der Name auf uns zeigt, besorgen wir das Zertifikat.',
  ].join('\n');

  return (
    <div className="mt-1.5 rounded bg-amber-50 px-2 py-2 text-xs text-amber-900">
      <p className="flex gap-2">
        <AlertTriangle size={13} className="mt-0.5 shrink-0" />
        <span>
          Bei einer Kundendomain muss <strong>der Kunde</strong> den Eintrag setzen. Wir legen ihn
          nicht an und können ihn beim Umzug nicht mitnehmen — ein Serverwechsel wird damit zur
          Absprache.
        </span>
      </p>

      {!vollstaendig ? (
        <p className="mt-1.5 pl-5 text-amber-700">
          Sobald Adresse und Server gewählt sind, stehen hier die konkreten Einträge zum Kopieren.
        </p>
      ) : (
        <div className="mt-2 pl-5">
          <div className="overflow-x-auto">
            <table className="min-w-full border-separate border-spacing-y-0.5">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-amber-700">
                  <th className="pr-3 text-left font-medium">Typ</th>
                  <th className="pr-3 text-left font-medium">Name</th>
                  <th className="text-left font-medium">Wert</th>
                </tr>
              </thead>
              <tbody className="align-top">
                <tr>
                  <td className="pr-3 font-mono">A</td>
                  <td className="pr-3"><Kopierbar wert={recordName} /></td>
                  <td><Kopierbar wert={ipv4!} /></td>
                </tr>
                {ipv6 && (
                  <tr>
                    <td className="pr-3 font-mono">AAAA</td>
                    <td className="pr-3"><Kopierbar wert={recordName} /></td>
                    <td><Kopierbar wert={ipv6} /></td>
                  </tr>
                )}
                {wwwEingerichtet && (
                  <>
                    <tr>
                      <td className="pr-3 font-mono">A</td>
                      <td className="pr-3"><Kopierbar wert="www" /></td>
                      <td><Kopierbar wert={ipv4!} /></td>
                    </tr>
                    {ipv6 && (
                      <tr>
                        <td className="pr-3 font-mono">AAAA</td>
                        <td className="pr-3"><Kopierbar wert="www" /></td>
                        <td><Kopierbar wert={ipv6} /></td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>

          {!istApex && domain && (
            <p className="mt-1.5 leading-snug text-amber-700">
              Der Name steht hier <strong>relativ zur Zone</strong>{' '}
              <span className="font-mono">{domain}</span> — so erwarten es die meisten
              Oberflächen. Verlangt eure den vollen Namen, ist es{' '}
              <span className="font-mono">{hostname}</span>.
            </p>
          )}

          {istApex && (
            <p className="mt-1.5 leading-snug text-amber-700">
              Das ist die Domain <strong>selbst</strong>, keine Subdomain. In fast jeder
              DNS-Oberfläche heißt der Eintrag dort <code className="font-mono">@</code> oder das
              Namensfeld bleibt <strong>leer</strong> — trägt man den vollen Namen ein, entsteht{' '}
              <span className="font-mono">{hostname}.{domain}</span>. Und ein{' '}
              <strong>CNAME ist am Apex nicht zulässig</strong>, auch nicht auf unseren Hostnamen:
              nur A und AAAA.
            </p>
          )}

          {/* ══ www ═══════════════════════════════════════════════════════
              Nicht blind mitempfehlen. Zeigt www auf uns, ohne dass der Proxy
              den Namen kennt, landen Besucher auf dessen Standardseite und
              bekommen einen Zertifikatsfehler — genau die Ursache, an der am
              13.09.2026 ojfm-dev einen halben Tag lang hing. */}
          {istApex && !wwwEingerichtet && wwwName && (
            <p className="mt-1.5 leading-snug text-amber-700">
              Soll <span className="font-mono">{wwwName}</span> auch funktionieren, muss die
              Adresse <strong>zuerst hier im PC</strong> ergänzt werden. Sonst zeigt der Eintrag
              zwar auf uns, der Proxy kennt den Namen aber nicht — und Besucher bekommen einen
              Zertifikatsfehler statt der Seite.
            </p>
          )}

          <p className="mt-1.5 leading-snug text-amber-700">
            TTL: Standard genügt. Das Zertifikat besorgen wir, sobald der Name auf uns zeigt —
            vorher lässt es sich nicht ausstellen, deshalb kommt der Eintrag vor dem Ausrollen.
            {serverName && <> Ziel ist <span className="font-mono">{serverName}</span>.</>}
          </p>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>
              <Kopierbar wert={alles} beschriftung="Alles kopieren" />
              <span className="ml-1 text-amber-700">— zum Weiterschicken</span>
            </span>
            <button
              type="button"
              onClick={() => pruefen.mutate()}
              disabled={pruefen.isPending}
              className="inline-flex items-center gap-1 rounded border border-amber-300 px-1.5 py-0.5 text-xs font-medium text-amber-900 hover:bg-white disabled:opacity-50"
            >
              {pruefen.isPending ? <Loader2 size={11} className="animate-spin" /> : <Search size={11} />}
              Zeigt es schon hierher?
            </button>
          </div>

          {/* ══ DIE FRAGE DANACH ═══════════════════════════════════════════
              „Der Kunde muss einen Eintrag setzen" wird unmittelbar gefolgt
              von „hat er?". Ohne Antwort darauf bleibt nur, auszurollen und zu
              sehen, ob es geht — und wenn nicht, weiss man immer noch nicht,
              ob es am DNS lag. */}
          {pruefen.data && (
            <div className="mt-1.5 rounded bg-white/70 px-2 py-1.5">
              {pruefen.data.passtV4 === true ? (
                <p className="flex items-center gap-1.5 text-emerald-700">
                  <CheckCircle2 size={12} /> Der Name zeigt bereits auf diesen Wirt.
                  {pruefen.data.passtV6 === false && (
                    <span className="text-amber-700">Der AAAA-Eintrag fehlt aber noch.</span>
                  )}
                </p>
              ) : !pruefen.data.a.length && !pruefen.data.aaaa.length ? (
                <p className="text-amber-800">
                  Der Name löst noch gar nicht auf — entweder ist der Eintrag nicht gesetzt oder er
                  ist noch nicht überall angekommen.
                </p>
              ) : (
                <>
                  <p className="text-amber-800">
                    Zeigt auf{' '}
                    <span className="font-mono">
                      {[...pruefen.data.a, ...pruefen.data.aaaa].join(', ')}
                    </span>
                  </p>
                  {pruefen.data.hinweis && (
                    <p className="mt-0.5 leading-snug text-amber-700">{pruefen.data.hinweis}</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
